<template>
  <div class="vue-avatar--wrapper" :style="[style, customStyle]" aria-hidden="true">
    <!-- this img is not displayed; it is used to detect failure-to-load of div background image -->
    <img v-if="isImage" alt="" style="display: none" :src="src" @error="onImgError" />
    <span v-show="!isImage">{{ userInitial }}</span>
  </div>
</template>
s
<script lang="ts">
/* eslint-disable no-bitwise */
import { computed, defineComponent, onMounted, ref } from 'vue'

const getInitials = (username = '') => {
  const parts = username.split(/[ -]/)
  let initials = ''
  for (const i of parts) {
    initials += i.charAt(0)
  }
  if (initials.length > 3 && initials.search(/[A-Z]/) !== -1) {
    initials = initials.replace(/[a-z]+/g, '')
  }
  initials = initials.substring(0, 3).toUpperCase()
  return initials
}

const lightenColor = (hex: string, amt: number) => {
  let usePound = false
  if (hex[0] === '#') {
    // eslint-disable-next-line no-param-reassign
    hex = hex.slice(1)
    usePound = true
  }
  const num = parseInt(hex, 16)
  let r = (num >> 16) + amt
  if (r > 255) r = 255
  else if (r < 0) r = 0
  let b = ((num >> 8) & 0x00ff) + amt
  if (b > 255) b = 255
  else if (b < 0) b = 0
  let g = (num & 0x0000ff) + amt
  if (g > 255) g = 255
  else if (g < 0) g = 0
  return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16)
}

export default defineComponent({
  name: 'Avatar',
  props: {
    username: {
      type: String,
    },
    initials: {
      type: String,
    },
    backgroundColor: {
      type: String,
    },
    color: {
      type: String,
    },
    customStyle: {
      type: Object as any,
      default: () => null,
    },
    inline: {
      type: Boolean,
    },
    size: {
      type: Number,
      default: 50,
    },
    src: {
      type: String,
    },
    rounded: {
      type: Boolean,
      default: true,
    },
    lighten: {
      type: Number,
      default: 80,
    },
    parser: {
      type: Function,
      default: getInitials,
      validator: (parser: any) => typeof parser('John', getInitials) === 'string',
    },
  },
  emits: ['avatar-initials'],
  setup(props, ctx) {
    const backgroundColors = ref([
      '#F44336',
      '#FF4081',
      '#9C27B0',
      '#673AB7',
      '#3F51B5',
      '#2196F3',
      '#03A9F4',
      '#00BCD4',
      '#009688',
      '#4CAF50',
      '#8BC34A',
      '#CDDC39',
      '#FFC107',
      '#FF9800',
      '#FF5722',
      '#795548',
      '#9E9E9E',
      '#607D8B',
    ])
    const imgError = ref(false)

    const isImage = computed(() => !imgError.value && Boolean(props.src))

    const randomBackgroundColor = (seed: any, colors: any) => colors[seed % colors.length]

    const background = computed(() => {
      if (!isImage.value) {
        const randomBgColor = randomBackgroundColor(
          props.username?.length || 0,
          backgroundColors.value,
        )
        return props.backgroundColor || randomBgColor
      }
      return props.backgroundColor
    })
    const fontColor = computed(() => {
      if (!isImage.value) {
        return props.color || lightenColor(background.value, props.lighten)
      }
      return props.color
    })
    const style = computed(() => {
      const mStyle = {
        display: props.inline ? 'inline-flex' : 'flex',
        width: `${props.size}px`,
        height: `${props.size}px`,
        borderRadius: props.rounded ? '50%' : 0,
        lineHeight: `${props.size + Math.floor(props.size / 20)}px`,
        fontWeight: 'bold',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        userSelect: 'none',
      }
      const imgBackgroundAndFontStyle = {
        background: `transparent url('${props.src}') no-repeat scroll 0% 0% / ${props.size}px ${props.size}px content-box border-box`,
      }
      const initialBackgroundAndFontStyle = {
        backgroundColor: background.value,
        font: `${Math.floor(props.size / 2.5)}px/${props.size}px Helvetica, Arial, sans-serif`,
        color: fontColor.value,
      }
      const backgroundAndFontStyle = isImage.value
        ? imgBackgroundAndFontStyle
        : initialBackgroundAndFontStyle
      Object.assign(mStyle, backgroundAndFontStyle)

      return mStyle
    })
    const userInitial = computed(() => {
      if (!isImage.value) {
        return props.initials || props.parser(props.username, getInitials)
      }
      return ''
    })

    onMounted(() => {
      if (!isImage.value) {
        ctx.emit('avatar-initials', props.username, userInitial.value)
      }
    })

    const initial = getInitials

    const onImgError = () => {
      imgError.value = true
    }

    return {
      backgroundColors,
      imgError,

      // computed
      isImage,
      background,
      fontColor,
      style,
      userInitial,

      // Methods
      initial,
      onImgError,
      lightenColor,
      randomBackgroundColor,
    }
  },
})
</script>
